import RecordedChampion from '../../../components/RecordedChampion.jsx'
/* ========================================================================= *
 * THE SWEEP — the button, finally given a face.
 *
 * Brian: "Is there a button to push this?"
 *
 * The answer was: on the server, yes. `POST /api/champion-sweep` has existed
 * since he asked for it — *"I need a button that forces find championships from
 * certain dates. We are not inventing champions so we don't see into the
 * future."* — and its own source comment calls it "the button". Nothing in this
 * app had ever called it. The button was built and never got a face.
 *
 * WHAT IT DOES AND DOES NOT DO, because that distinction is the whole value:
 *   IT DOES     tell you every championship in a window that still has nobody
 *               recorded as winning, ordered longest-failing-first.
 *   IT DOES NOT hunt. It writes nothing. Hunting is a separate, human-started
 *               step — the 7am sweep, or Hunt champion on a row.
 *
 * So this is a READ that produces a worklist. Every row hands off to the
 * Summarizer, which is what closes the loop between "what needs a champion"
 * and "go and get one".
 * ========================================================================= */

import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import HuntNow from './HuntNow.jsx'
import styles from './pursuit.module.css'

/** Today, as YYYY-MM-DD in local terms — the latest end the server will accept. */
function todayISO() {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}
function daysAgoISO(n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

export default function SweepPanel() {
  /* The provider's own bound helpers — same call shape every other module
     uses, so this cannot drift from how permissions are asked elsewhere. */
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.SYSTEM_RUN)
  const [from, setFrom] = useState(daysAgoISO(30))
  const [to, setTo] = useState(todayISO())
  const [state, setState] = useState({ status: 'idle' })
  const [open, setOpen] = useState(false)

  const run = async () => {
    setState({ status: 'loading' })
    try {
      const r = await api.championSweep({ from, to, limit: 200 })
      setState({ status: 'done', data: r })
    } catch (err) {
      /* ⚠️ THE COLD-CACHE TIMEOUT, and why it gets its own message.
       *
       * The sweep reads the WHOLE championship universe — ~12,700 records,
       * ~128 sequential HubSpot pages (`gaps.js#universe`). On a cold cache
       * that is minutes, and the gateway gives up at 30 seconds. So the FIRST
       * press after ten idle minutes fails, every time, no matter what.
       *
       * It is not a broken button, and it must not read like one. The server
       * keeps walking after the client gives up and caches the result for ten
       * minutes, so the SECOND press returns instantly. Telling somebody that
       * is the difference between "press it again" and "this is broken" —
       * which matters most in exactly the moment you can least afford it.
       *
       * The real fix is a server-side warmer on that cache; that lives in
       * sr-portal-backend, which is not modified from here (hard rule 4). */
      const status = err?.status
      const timeoutish =
        status === 504 || status === 502 || status === 408 || status === 0 ||
        /timeout|network|failed to fetch|could not reach/i.test(err?.message || '')

      if (timeoutish) {
        setState({
          status: 'error',
          kind: 'cold',
          message:
            'First run after a quiet spell has to read the whole championship universe — about 12,700 records — and that takes longer than the 30-second gateway allows.',
          hint:
            'The read carries on server-side and is cached for ten minutes, so pressing Run sweep again now will come back straight away.',
        })
        return
      }

      /* The server's refusals are USEFUL — a future window comes back with the
       * reason and the latest allowed date. Show what it said rather than a
       * generic failure. */
      const body = err?.body || {}
      setState({
        status: 'error',
        message: body.reason || body.error || err?.message || 'The sweep failed.',
        problems: body.problems || [],
        latestAllowed: body.latestAllowed || '',
      })
    }
  }

  if (!allowed) {
    /* Denied renders with the reason rather than vanishing — a control that
     * silently is not there reads as a broken page. */
    return (
      <div className={styles.sweepDenied}>
        Champion sweep — {why(ACTIONS.SYSTEM_RUN) || 'available to exec and ops.'}
      </div>
    )
  }

  const d = state.data

  return (
    <section className={styles.sweep}>
      <button
        type="button"
        className={styles.sweepHead}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.sweepTitle}>Champion sweep</span>
        <span className={styles.sweepSub}>
          Who still has nobody recorded as winning, in a date window
        </span>
        <span className={styles.sweepChevron}>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className={styles.sweepBody}>
          <div className={styles.sweepControls}>
            <label className={styles.sweepField}>
              <span>From</span>
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className={styles.sweepField}>
              <span>To</span>
              {/* The server refuses a future window outright. Capping the input
                  at today makes that impossible to reach by accident, while the
                  server stays the thing that actually enforces it. */}
              <input type="date" value={to} max={todayISO()} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button
              type="button"
              className={`btn btn-primary ${styles.sweepRun}`}
              onClick={run}
              disabled={state.status === 'loading'}
            >
              {state.status === 'loading' ? 'Sweeping…' : 'Run sweep'}
            </button>
            <span className={styles.sweepNote}>
              Reads only — nothing is written, and no paid search runs.
            </span>
          </div>

          {state.status === 'error' && (
            /* A cold-cache timeout is a WAIT, not a fault, and it is styled as
               one — showing it in the same red as a real failure is what makes
               somebody think the button is broken. */
            <div className={state.kind === 'cold' ? styles.sweepWait : styles.sweepError}>
              {state.message}
              {state.hint && <div className={styles.sweepHint}>{state.hint}</div>}
              {state.problems?.length > 0 && (
                <ul>{state.problems.map((p) => <li key={p}>{p}</li>)}</ul>
              )}
              {state.kind === 'cold' && (
                <button type="button" className={styles.sweepFix} onClick={run}>
                  Run sweep again
                </button>
              )}
              {state.latestAllowed && (
                <button type="button" className={styles.sweepFix} onClick={() => setTo(state.latestAllowed)}>
                  Set the end date to {state.latestAllowed}
                </button>
              )}
            </div>
          )}

          {state.status === 'done' && d && (
            <>
              <div className={styles.sweepScore}>
                <b>{d.needChampion}</b> still need a champion between {d.window?.from} and {d.window?.to}
                {d.neverSearched != null && (
                  <span className={styles.sweepDim}> · {d.neverSearched} never searched</span>
                )}
              </div>

              {/* THE COVERAGE LINE, verbatim from the server. It is the answer
                  to "did the button actually look at everything?" — including
                  the dateless records, which an earlier version silently
                  skipped. Quoting it rather than re-deriving it here means the
                  number on screen and the number the server computed cannot
                  drift apart. */}
              {d.coverage?.explain && (
                <p className={styles.sweepCoverage}>{d.coverage.explain}</p>
              )}

              {d.records?.length > 0 ? (
                <ul className={styles.sweepList}>
                  {d.records.map((r) => (
                    <li key={r.id} className={styles.sweepRow}>
                      <div className={styles.sweepRowMain}>
                        <span className={styles.sweepName}>Championship: {r.name}</span>
                        {Object.hasOwn(r,'champion') || Object.hasOwn(r,'current_champion') || Object.hasOwn(r,'winningSchool')
                          ? <RecordedChampion record={{...r,effDate:r.effDate||r.date}} />
                          : <span>Champion: not included in this sweep read. Review the saved record before choosing research or preparation.</span>}
                        <span className={styles.sweepMeta}>
                          {[r.sport, r.level, r.state].filter(Boolean).join(' · ')}
                          {r.date ? ` · ${r.date}` : ' · no date'}
                          {r.dateConfidence && r.dateConfidence !== 'none'
                            ? ` (${r.dateConfidence})` : ''}
                        </span>
                        {/* ATTEMPTS ARE THE DIAGNOSIS. A record searched nine
                            times with no champion is a missing SOURCE, not a
                            detection problem — those want a human, not another
                            sweep. One never searched is just untouched. */}
                        <span className={r.attempts > 2 ? styles.sweepFlag : styles.sweepDim}>
                          {r.attempts > 0
                            ? `${r.attempts} attempt${r.attempts === 1 ? '' : 's'} · ${r.lastSearch.replace(/_/g, ' ')}`
                            : 'never searched'}
                        </span>
                      </div>
                      {/* The swept worklist hands straight to the hunt — find what
                          needs a champion, then get one, without leaving. */}
                      <HuntNow record={r} compact />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.sweepEmpty}>
                  Nothing in this window is missing a champion. That is a real result — widen the
                  dates to look further back.
                </p>
              )}

              {d.records && d.needChampion > d.records.length && (
                <p className={styles.sweepDim}>
                  Showing {d.records.length} of {d.needChampion} — narrow the window to see the rest.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}
